import { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  Box,
} from "@mui/material";

const DEFAULT_FORM_STATE = {
  id: null,
  name: "",
  configuration: {},
  services: {},
};

const COURIER_SERVICES_TEMPLATES = {
  FEDEX: {
    FEDEX_PRIORITY: { min_weight: "0", max_weight: "" },
    FEDEX_PRIORITY_FREIGHT: { min_weight: "", max_weight: "" },
  },
};
/**
 * @typedef {object} AdminCourierInitialData
 * @property {number} id
 * @property {string} name
 * @property {object} configuration
 * @property {object} services
 */

/**
 * A modal for editing an Admin Setting Courier (BYOC)
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {function(AdminCourierInitialData): Promise<void>} props.onConfirm - Async function to call with form data when saving
 * @param {boolean} props.loading - Whether the form is currently submitting
 * @param {AdminCourierInitialData | null} props.initialData - The data to populate the form with (for "Edit" mode)
 * @param {object} props.templates - Object containing the required fields per courier
 */
const EditSettingCourierModal = ({
  open,
  onClose,
  onConfirm,
  initialData,
  loading = false,
  templates = {},
}) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  const isFormValid =
    formData.name.trim() !== "" &&
    Object.keys(templates[formData.name] || {}).every(
      (key) =>
        formData.configuration[key] &&
        String(formData.configuration[key]).trim() !== "",
    ) &&
    (formData.name !== "FEDEX" ||
      Object.keys(formData.services || {}).every(
        (serviceKey) =>
          formData.services[serviceKey]?.min_weight !== "" &&
          formData.services[serviceKey]?.max_weight !== "",
      ));

  useEffect(() => {
    if (open && initialData) {
      const initialConfig = templates[initialData.name]
        ? { ...templates[initialData.name] }
        : {};
      const initialServicesTemplate = COURIER_SERVICES_TEMPLATES[
        initialData.name
      ]
        ? JSON.parse(
            JSON.stringify(COURIER_SERVICES_TEMPLATES[initialData.name]),
          )
        : {};

      setFormData({
        id: initialData.id,
        name: initialData.name,
        configuration: {
          ...initialConfig,
          ...(initialData.configuration || {}),
        },
        services: {
          ...initialServicesTemplate,
          ...(initialData.services || {}),
        },
      });
      setFormErrors({});
    }
  }, [open, initialData, templates]);

  const handleConfigChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      configuration: {
        ...previous.configuration,
        [name]: value,
      },
    }));

    if (formErrors[name]) {
      setFormErrors((previous) => ({ ...previous, [name]: null }));
    }
  };

  const handleServiceWeightChange = (serviceKey, field, value) => {
    const sanitizedValue = String(value).replace(/-/g, "");

    setFormData((previous) => ({
      ...previous,
      services: {
        ...previous.services,
        [serviceKey]: {
          ...previous.services[serviceKey],
          [field]: sanitizedValue,
        },
      },
    }));

    if (formErrors[`${serviceKey}_${field}`]) {
      setFormErrors((previous) => ({
        ...previous,
        [`${serviceKey}_${field}`]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) {
      errors.submit = "Datos de proveedor inválidos";
    } else {
      const requiredFields = Object.keys(templates[formData.name] || {});
      requiredFields.forEach((field) => {
        if (
          !formData.configuration[field] ||
          String(formData.configuration[field]).trim() === ""
        ) {
          errors[field] = "Este campo es requerido";
        }
      });

      if (formData.name === "FEDEX" && formData.services) {
        const priorityMin = parseFloat(
          formData.services.FEDEX_PRIORITY?.min_weight,
        );
        const priorityMax = parseFloat(
          formData.services.FEDEX_PRIORITY?.max_weight,
        );
        const freightMin = parseFloat(
          formData.services.FEDEX_PRIORITY_FREIGHT?.min_weight,
        );
        const freightMax = parseFloat(
          formData.services.FEDEX_PRIORITY_FREIGHT?.max_weight,
        );

        if (isNaN(priorityMin) || priorityMin < 0)
          errors.FEDEX_PRIORITY_min_weight = "Mínimo inválido";
        if (isNaN(priorityMax) || priorityMax <= priorityMin)
          errors.FEDEX_PRIORITY_max_weight = "Debe ser mayor al mínimo";
        if (isNaN(freightMin) || freightMin < 0)
          errors.FEDEX_FREIGHT_min_weight = "Mínimo inválido";
        if (isNaN(freightMax) || freightMax <= freightMin)
          errors.FEDEX_FREIGHT_max_weight = "Debe ser mayor al mínimo";

        if (
          !errors.FEDEX_PRIORITY_max_weight &&
          !errors.FEDEX_FREIGHT_min_weight
        ) {
          if (
            (priorityMin <= freightMin && priorityMax >= freightMin) ||
            (freightMin <= priorityMin && freightMax >= priorityMin)
          ) {
            errors.submit =
              "Los rangos de peso no pueden solaparse ni tener valores iguales.";
          }
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;

    onConfirm(formData);
  };

  // Helpers visuales
  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isSecretField = (key) => {
    const lowerKey = key.toLowerCase();
    return (
      lowerKey.includes("secret") ||
      lowerKey.includes("password") ||
      lowerKey.includes("token") ||
      lowerKey.includes("clave")
    );
  };

  if (!initialData) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar Credenciales - {formData.name}</DialogTitle>

      <DialogContent>
        {formData.name && templates[formData.name] ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Actualiza las llaves de configuración para tu cuenta de{" "}
              {formData.name}.
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(templates[formData.name]).map((fieldKey) => (
                <Grid item xs={12} sm={12} key={fieldKey}>
                  <TextField
                    margin="dense"
                    id={fieldKey}
                    name={fieldKey}
                    label={formatLabel(fieldKey)}
                    type={isSecretField(fieldKey) ? "password" : "text"}
                    fullWidth
                    variant="outlined"
                    value={formData.configuration[fieldKey] || ""}
                    onChange={handleConfigChange}
                    error={!!formErrors[fieldKey]}
                    helperText={formErrors[fieldKey]}
                    required
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No se encontraron plantillas de configuración para este proveedor.
          </Alert>
        )}

        {formData.name === "FEDEX" && formData.services && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px dashed #ccc",
              borderRadius: 2,
              bgcolor: "#fafafa",
            }}
          >
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ mb: 2, fontWeight: "bold" }}
            >
              Enrutamiento Automático por Peso
            </Typography>

            {Object.keys(formData.services).map((serviceKey) => (
              <Box key={serviceKey} sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "600", mb: 1, color: "#333" }}
                >
                  {serviceKey.replaceAll("_", " ")}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      size="small"
                      label="Peso Mínimo (kg)"
                      value={formData.services[serviceKey]?.min_weight || ""}
                      onChange={(e) =>
                        handleServiceWeightChange(
                          serviceKey,
                          "min_weight",
                          e.target.value,
                        )
                      }
                      error={!!formErrors[`${serviceKey}_min_weight`]}
                      helperText={formErrors[`${serviceKey}_min_weight`]}
                      inputProps={{ step: "0.1", min: "0" }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      size="small"
                      label="Peso Máximo (kg)"
                      value={formData.services[serviceKey]?.max_weight || ""}
                      onChange={(e) =>
                        handleServiceWeightChange(
                          serviceKey,
                          "max_weight",
                          e.target.value,
                        )
                      }
                      error={!!formErrors[`${serviceKey}_max_weight`]}
                      helperText={formErrors[`${serviceKey}_max_weight`]}
                      inputProps={{ step: "0.1", min: "0" }}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Guardar Cambios"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSettingCourierModal;
